import React from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { useCancelledOrders } from '../../lib/admin-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, formatRelativeTime } from '../../lib/utils';
import { XCircle, ShoppingBag } from 'lucide-react';
export function CancelledOrdersPage() {
  const slug = useTenantSlug();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  // Get tenant context for role-aware layout
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const {
    orders,
    loading: ordersLoading
  } = useCancelledOrders(restaurant?.id);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-3503" data-mp="2gll3jijsjgno">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-3504" data-mp="2bjvozsxd0kuu" />
      </div>;
  }
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-3505" data-mp="2qgd14mddi3ig">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-3506" data-mp="2ljcua98p8wqa">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-3507" data-mp="1xsh3nffzc7km">
          <div data-id="element-3508" data-mp="1sup4c0d09j6e">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-3509" data-mp="1sv8mni6ki9ky">
              <XCircle className="w-6 h-6 text-red-500" data-id="element-3510" data-mp="27j265dyrxw8t" />
              Pedidos Cancelados
            </h1>
            <p className="text-gray-500" data-id="element-3511" data-mp="22r26lmmm95cr">
              Historial de pedidos cancelados ({orders.length})
            </p>
          </div>
        </div>

        <Card data-id="element-3512" data-mp="1f06c2hqrzw1t">
          <CardHeader data-id="element-3513" data-mp="1a3o4zb7z3lmb">
            <CardTitle className="flex items-center gap-2" data-id="element-3514" data-mp="1a2d4bk9tqtzf">
              <ShoppingBag className="w-5 h-5 text-gray-400" data-id="element-3515" data-mp="1zrjwgqomn4x1" />
              Cancelados Recientes
            </CardTitle>
          </CardHeader>
          <CardContent data-id="element-3516" data-mp="23s3tlu97a8j6">
            {ordersLoading ? <div className="space-y-4" data-id="element-3517" data-mp="28jvi7z8sq46o">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 w-full" data-id="element-3518" data-mp="28m80yemdoiks" />)}
              </div> : orders.length > 0 ? <div className="overflow-x-auto" data-id="element-3519" data-mp="1gthcwreum06m">
                <table className="w-full text-sm text-left" data-id="element-3520" data-mp="1gush2sc7h9y9">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-3521" data-mp="1kvcbho5e9zgp">
                    <tr data-id="element-3522" data-mp="1psul7gr8kipr">
                      <th className="px-4 py-3 rounded-l-lg" data-id="element-3523" data-mp="1psmcm125bzkd">Pedido</th>
                      <th className="px-4 py-3" data-id="element-3524" data-mp="1ut0aiqa19t4o">Cliente</th>
                      <th className="px-4 py-3" data-id="element-3525" data-mp="1uq49gt2w2oc0">Teléfono</th>
                      <th className="px-4 py-3" data-id="element-3526" data-mp="317rdr0mmpq1v">Total</th>
                      <th className="px-4 py-3" data-id="element-3527" data-mp="367ds5de71cc9">Artículos</th>
                      <th className="px-4 py-3" data-id="element-3528" data-mp="365sydpzpdxfg">Estado</th>
                      <th className="px-4 py-3 rounded-r-lg" data-id="element-3529" data-mp="ukk27wy5v2tu">Fecha</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3530" data-mp="pmigf49copeo">
                    {orders.map(order => <tr key={order.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors" data-id="element-3531" data-mp="ktpdmlxfzr58">
                        <td className="px-4 py-3" data-id="element-3532" data-mp="bu2mqu2ca3qf">
                          <span className="font-medium text-gray-900" data-id="element-3533" data-mp="brpw7siiml1r">
                            #{order.order_number}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700" data-id="element-3534" data-mp="1w4wk10x5wv6">
                          {order.customer_name || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-500" data-id="element-3535" data-mp="1w9e4hodthabb">
                          {order.customer_phone || '—'}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900" data-id="element-3536" data-mp="1rbciovp0aww5">
                          {formatCurrency(order.total)}
                        </td>
                        <td className="px-4 py-3 text-gray-500" data-id="element-3537" data-mp="24alof4k0ayjv">
                          {order.items.length}{' '}
                          {order.items.length === 1 ? 'artículo' : 'artículos'}
                        </td>
                        <td className="px-4 py-3" data-id="element-3538" data-mp="1ual1l8mxi0ax">
                          <Badge variant="danger" data-id="element-3539" data-mp="1gl152kjxxza2">Cancelado</Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-500" data-id="element-3540" data-mp="1lbhkga7bnuz2">
                          <div className="text-sm" data-id="element-3541" data-mp="1lcslw3daoqn0">
                            {new Date(order.created_at).toLocaleDateString('es-CR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-3542" data-mp="iywsnogcwfar">
                            {formatRelativeTime(order.created_at)}
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div> : <div className="text-center py-12" data-id="element-3543" data-mp="mzqgyixpnw46">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3544" data-mp="n34l9wpxqfe9">
                  <XCircle className="w-8 h-8 text-gray-300" data-id="element-3545" data-mp="wz7lgx1883yk" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-1" data-id="element-3546" data-mp="wx4vue3zkcud">
                  Sin pedidos cancelados
                </h3>
                <p className="text-sm text-gray-400" data-id="element-3547" data-mp="16oitshgah4le">
                  No hay pedidos cancelados registrados.
                </p>
              </div>}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>;
}